import { useCallback, useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { ErreurApi } from '../../api/client.js';
import { gererClic, useNaviguer } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS, OMBRES, POLICE_EQUIPE, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';

/**
 * Liste transversale, tous dossiers confondus, filtrable (texte, « À traiter seulement »), avec un lien vers le dossier : le gabarit
 * commun de Facturation (D08) et Documents (D09) quand aucun dossier n'est ouvert. Équipe MAAT seulement. Les données viennent d'une
 * source (agrégation par dossier sur les routes du contrat) ; ce gabarit n'affiche que ce qu'elle lui donne.
 */
export interface LigneParDossier {
  dossierId: string;
  titre: string;
  reference: string;
}

export interface CelluleListe {
  titre: string;
  contenu: ReactNode;
  /** Colonne de chiffres : alignée à droite. */
  nombre?: boolean;
}

export interface PropsListeParDossier<L extends LigneParDossier> {
  /** Valeur de `data-liste` : « facturation » ou « documents ». */
  nom: string;
  titre: string;
  intro: string;
  chargement: string;
  erreur: string;
  vide: string;
  largeur: number;
  charger: () => Promise<L[]>;
  cellules: (ligne: L) => CelluleListe[];
  aTraiter: (ligne: L) => boolean;
  lien: (ligne: L) => { vers: string; libelle: string; aria: string };
}

const LARGEUR_TABLEAU = 768;
const carte: CSSProperties = { background: COULEURS.blanc, border: `1px solid ${COULEURS.bordure}`, borderRadius: 12, padding: 16 };
const champ: CSSProperties = {
  minHeight: CIBLE_MIN,
  boxSizing: 'border-box',
  padding: '0 12px',
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  font: 'inherit',
  color: COULEURS.marineFonce,
  background: COULEURS.blanc,
  flex: '1 1 220px',
};
const cellule: CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  verticalAlign: 'top',
  borderBottom: `1px solid ${COULEURS.bordure}`,
};
const lienStyle: CSSProperties = {
  ...styleBoutonPrincipal,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: CIBLE_MIN,
  textDecoration: 'none',
  boxSizing: 'border-box',
};
const sansMarge: CSSProperties = { margin: 0 };

/** Vrai lien (nouvel onglet possible) qui navigue sans recharger au clic simple ; son nom accessible dit de quel dossier il s'agit. */
function LienAction({ vers, libelle, aria }: { vers: string; libelle: string; aria: string }): ReactElement {
  const naviguer = useNaviguer();
  return (
    <a href={vers} aria-label={aria} style={lienStyle} onClick={(e) => gererClic(e, vers, naviguer)}>
      {libelle}
    </a>
  );
}

type Etat<L> = { statut: 'chargement' } | { statut: 'erreur'; message: string } | { statut: 'pret'; lignes: L[] };

/** Largeur de la fenêtre, remesurée à chaque redimensionnement (la coquille ne la transmet pas aux écrans). */
export function useLargeurFenetre(): number {
  const lire = (): number => globalThis.innerWidth ?? 1280;
  const [largeur, setLargeur] = useState(lire);
  useEffect(() => {
    const mesurer = (): void => setLargeur(lire());
    mesurer();
    globalThis.addEventListener?.('resize', mesurer);
    return () => globalThis.removeEventListener?.('resize', mesurer);
  }, []);
  return largeur;
}

const normaliser = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export function ListeParDossier<L extends LigneParDossier>(p: PropsListeParDossier<L>): ReactElement {
  const [etat, setEtat] = useState<Etat<L>>({ statut: 'chargement' });
  const [texte, setTexte] = useState('');
  const [seulementATraiter, setSeulementATraiter] = useState(false);
  const { charger, erreur } = p;
  const lire = useCallback(async (): Promise<void> => {
    setEtat({ statut: 'chargement' });
    try {
      setEtat({ statut: 'pret', lignes: await charger() });
    } catch (e) {
      setEtat({ statut: 'erreur', message: e instanceof ErreurApi ? e.messageUtilisateur : erreur });
    }
  }, [charger, erreur]);
  useEffect(() => {
    void lire();
  }, [lire]);

  const requete = normaliser(texte.trim());
  const lignes =
    etat.statut === 'pret'
      ? etat.lignes.filter(
          (l) => (!requete || normaliser(`${l.titre} ${l.reference}`).includes(requete)) && (!seulementATraiter || p.aTraiter(l)),
        )
      : [];
  const tableau = p.largeur >= LARGEUR_TABLEAU;

  return (
    <section data-liste={p.nom} style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: POLICE_EQUIPE }}>
      <h1 style={{ margin: 0, color: COULEURS.marine, fontSize: 28 }}>{p.titre}</h1>
      <p style={sansMarge}>{p.intro}</p>
      {etat.statut === 'chargement' ? <p role="status">{p.chargement}</p> : null}
      {etat.statut === 'erreur' ? (
        <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p style={{ ...sansMarge, color: COULEURS.rouge }}>{etat.message}</p>
          <button type="button" style={styleBouton} onClick={() => void lire()}>
            Réessayer
          </button>
        </div>
      ) : null}
      {etat.statut === 'pret' ? (
        <>
          <div
            data-carte-liste=""
            style={{
              ...carte,
              border: `1px solid ${COULEURS.bordureDouce}`,
              boxShadow: OMBRES.carte,
              padding: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, color: COULEURS.marine }}>Les dossiers</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <input
                type="search"
                aria-label="Chercher un dossier par nom ou référence"
                placeholder="Chercher un dossier par nom ou référence"
                value={texte}
                style={champ}
                onChange={(e) => setTexte(e.target.value)}
              />
              <button
                type="button"
                aria-pressed={seulementATraiter}
                style={{ ...styleBouton, ...(seulementATraiter ? { background: COULEURS.marine, color: COULEURS.blanc } : {}) }}
                onClick={() => setSeulementATraiter(!seulementATraiter)}
              >
                À traiter seulement
              </button>
            </div>
            {lignes.length === 0 ? (
              <p style={sansMarge}>{etat.lignes.length === 0 ? p.vide : 'Aucun dossier ne correspond à votre recherche.'}</p>
            ) : tableau ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <caption style={{ position: 'absolute', left: -9999 }}>{p.titre}, un dossier par ligne</caption>
                  <thead>
                    <tr>
                      <th scope="col" style={{ ...cellule, color: COULEURS.ardoise }}>
                        Dossier
                      </th>
                      {p.cellules(lignes[0] as L).map((c) => (
                        <th
                          key={c.titre}
                          scope="col"
                          style={{ ...cellule, color: COULEURS.ardoise, ...(c.nombre ? { textAlign: 'right' } : {}) }}
                        >
                          {c.titre}
                        </th>
                      ))}
                      <th scope="col" style={{ ...cellule, color: COULEURS.ardoise }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((l) => {
                      const lien = p.lien(l);
                      return (
                        <tr key={l.dossierId} data-dossier={l.dossierId}>
                          <td style={cellule}>
                            <strong>{l.titre}</strong>
                            <br />
                            <small style={{ color: COULEURS.ardoise }}>{l.reference}</small>
                          </td>
                          {p.cellules(l).map((c) => (
                            <td key={c.titre} style={{ ...cellule, ...(c.nombre ? { textAlign: 'right' } : {}) }}>
                              {c.contenu}
                            </td>
                          ))}
                          <td style={cellule}>
                            <LienAction {...lien} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {lignes.map((l) => {
                  const lien = p.lien(l);
                  return (
                    <li key={l.dossierId} data-dossier={l.dossierId} style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <span>
                        <strong>{l.titre}</strong>
                        <br />
                        <small style={{ color: COULEURS.ardoise }}>{l.reference}</small>
                      </span>
                      {p.cellules(l).map((c) => (
                        <span key={c.titre}>
                          <span style={{ color: COULEURS.ardoise }}>{`${c.titre} : `}</span>
                          {c.contenu}
                        </span>
                      ))}
                      <LienAction {...lien} />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
